import { FormControl, Grid } from '@mui/material';
import { CorePermissions } from 'app/appPermissions';
import { AutoCompleteDataModel, MprcAutoComplete } from 'components/MprcAutoComplete';
import { AutoCompleteSuggestions, ChannelAutoCompleteDataModel, ManagerAutoCompleteDataModel, TrainingAutoCompleteDataModel } from 'components/MprcAutoComplete/AutoCompleteDataModel';
import { AutoCompleteHierarchicalModel, TopicAutoCompleteHierarchicalModel, UnitAutoCompleteHierarchicalModel } from 'components/MprcAutoCompleteHierarchical/AutoCompleteHierarchicalModel';
import { MprcRequestSnackbarErrorReport } from 'components/MprcRequestErrorReport';
import { GroupInfo, GroupType, UnitTypeResponse } from 'generated/CoreApi_v1';
import { useCoreApi } from 'providers/CoreApiProvider';
import { useSession } from 'providers/SessionProvider';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AudienceOption } from 'views/foundation/group/components/AudienceSelectorDropdown';
import { GroupAutoComplete } from 'views/foundation/group/components/GroupAutoComplete';
import { TopicAutoCompleteHierarchical } from 'views/foundation/topic/components/TopicAutoCompleteHierarchical';
import { UnitAutoCompleteHierarchical } from 'views/foundation/unit/components/UnitAutoCompleteHierarchical';
import { ManagerAutoComplete } from 'views/foundation/user/components/ManagerAutoComplete';
import { UserAutoComplete } from 'views/foundation/user/components/UserAutoComplete';
import { ChannelAutoComplete } from 'views/knowledge/channels/components/ChannelAutoComplete';
import { TrainingAutoComplete } from 'views/lms/training/components/TrainingAutoComplete';

////////////////////////////////////////////////////////////////////////////

interface AudienceItem {
    group?: GroupInfo;
    autoComplete?: AutoCompleteDataModel | AutoCompleteHierarchicalModel;
}

interface AudienceSelectorDropdownProps {
    label?: string;
    placeholder?: string;
    onChange: (group?: GroupInfo | null) => void;
}

////////////////////////////////////////////////////////////////////////////

export const AudienceSelector: React.FC<AudienceSelectorDropdownProps> = (props) => {

    const { label, placeholder, onChange } = props;

    const api = useCoreApi();
    const { permissionExplorer } = useSession();
    const { t } = useTranslation("core");
    const inputRef = useRef<HTMLInputElement>(undefined);

    const [options, setOptions] = useState<AudienceOption[]>([]);
    const [audienceOption, setAudienceOption] = useState<AudienceOption>();
    const [selectedItem, setSelectedItem] = useState<AutoCompleteDataModel[]>([]);
    const [unitTypes, setUnitTypes] = useState<UnitTypeResponse[]>([]);
    const [internalGroups, setInternalGroups] = useState<GroupInfo[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    ////////////////////////////////////////////////////////////////////////////

    const getInternalGroup = (groupType: GroupType) => internalGroups.find(p => p.groupType == groupType);

    ///
    const getAudienceOption = (): AudienceOption[] => {
        const items: AudienceOption[] = [];

        if (permissionExplorer.hasOne([CorePermissions.GroupsViewGeneral])) {
            items.push({ groupType: GroupType.AllUsers, groupTypeName: t("Group.AudienceSelector.AllUsersOption"), group: getInternalGroup(GroupType.AllUsers) });
            items.push({ groupType: GroupType.InternalUsers, groupTypeName: t("Group.AudienceSelector.InternalUsersOption"), group: getInternalGroup(GroupType.InternalUsers) });
            items.push({ groupType: GroupType.ExternalUsers, groupTypeName: t("Group.AudienceSelector.ExternalUsersOption"), group: getInternalGroup(GroupType.ExternalUsers) });
        }

        items.push({ groupType: GroupType.OwnUser, groupTypeName: t("Group.AudienceSelector.UsersOption") });
        items.push({ groupType: GroupType.Static, groupTypeName: t("Group.AudienceSelector.GroupMembersOption") });

        if (permissionExplorer.hasOne([CorePermissions.GroupsViewGeneral])) {
            items.push({ groupType: GroupType.ChannelUsers, groupTypeName: t("Group.AudienceSelector.ChannelMembersOption") });
            items.push({ groupType: GroupType.TopicOptionalUsers, groupTypeName: t("Group.AudienceSelector.TopicOptionalUsersOption") });
            items.push({ groupType: GroupType.TopicRequiredUsers, groupTypeName: t("Group.AudienceSelector.TopicRequiredUsersOption") });
            items.push({ groupType: GroupType.TrainingApprovedUsers, groupTypeName: t("Group.AudienceSelector.TrainingApprovedUsersOption") });
        }

        unitTypes.forEach(item =>
            items.push({ groupType: GroupType.UnitUsers, groupTypeName: item.name + "...", groupTypeId: item.unitTypeId })
        );

        items.push({ groupType: GroupType.DirectSubordinates, groupTypeName: t("Group.AudienceSelector.DirectSubordinatesOption") });
        items.push({ groupType: GroupType.AllSubordinates, groupTypeName: t("Group.AudienceSelector.AllSubordinatesOption") });

        return items;
    };

    ///
    const getSuggestions = (searchText: string, pageNumber: number, pageSize: number): Promise<AutoCompleteSuggestions<AutoCompleteDataModel>> => {
        searchText = searchText.toLowerCase();
        return new Promise<AutoCompleteSuggestions<AutoCompleteDataModel>>((resolve) => {
            const filterOptions = searchText.length > 0
                ? options.filter(opt => opt.groupTypeName?.toLowerCase().includes(searchText))
                : [...options];

            resolve({
                items: filterOptions.map(x => ({
                    key: x.groupTypeId ?? x.groupType,
                    label: x.groupTypeName,
                    canDelete: true,
                })) as AutoCompleteDataModel[],
                totalRows: filterOptions.length
            });
        });
    };

    ///
    useEffect(() => {
        async function getData() {
            try {
                api.v1.unitType.getUnitTypes().then(response => setUnitTypes(response.data));
                api.v1.group.getInternalGroups().then(response => setInternalGroups(response.data));
            } catch (e) {
                MprcRequestSnackbarErrorReport(e);
            } finally {
                setLoading(false);
            }
        }
        if (loading) getData();
    }, [loading]);

    ///
    useEffect(() => {
        var selOption;
        if (selectedItem.length > 0) {
            selOption = getAudienceOption().find(x => x.groupType == selectedItem[0].key || x.groupTypeId == selectedItem[0].key);
        }
        setAudienceOption(selOption);
    }, [selectedItem]);

    ///
    useEffect(() => {
        onChange(audienceOption?.group ? audienceOption.group : null);
    }, [audienceOption]);

    ///
    useEffect(() => {
        setOptions(getAudienceOption());
    }, [unitTypes, internalGroups]);

    useEffect(() => {
        setTimeout(() => {
            if (inputRef) (inputRef as any)?.current?.focus();
        }, 300);
    }, [inputRef]);

    return (
        <Grid container width="100%" spacing={1} alignItems={'end'}>
            <Grid size="grow">
                <MprcAutoComplete
                    label={label ?? t("Group.AudienceSelector.SelectOptionPlaceholder")}
                    fullWidth
                    disabled={loading}
                    autoFocus
                    multiSelect={false}
                    inputRef={inputRef}
                    selectedItems={selectedItem}
                    placeholder={placeholder}
                    onChange={(items) => setSelectedItem(items)}
                    onGetSuggestions={getSuggestions}
                />
            </Grid>
            <Grid size="grow">
                {
                    audienceOption &&
                    <FormControl fullWidth>
                        <AudienceSelectorField
                            audienceOption={audienceOption}
                            onChange={(item) => {
                                if (item) setAudienceOption(item);
                            }}
                        />
                    </FormControl>
                }
            </Grid>
        </Grid>
    );
};

export interface AudienceSelectorFieldProps {
    audienceOption: AudienceOption;
    onChange: (audienceOption: AudienceOption | null) => void;
}

////////////////////////////////////////////////////////////////////////////

export const AudienceSelectorField: React.FC<AudienceSelectorFieldProps> = (props) => {

    const { audienceOption, onChange } = props;

    const { t } = useTranslation("core");

    const [selectedAudience, setSelectedAudience] = useState<AudienceItem | undefined>();

    ///
    const handleSelect = (group: GroupInfo | undefined, item: AutoCompleteDataModel | AutoCompleteHierarchicalModel | undefined) => {
        setSelectedAudience({ group, autoComplete: item });
        onChange({ ...audienceOption, group });
    };

    ///
    useEffect(() => {
        if (!audienceOption.group)
            setSelectedAudience(undefined);
    }, [audienceOption]);

    return (
        <>
            {(audienceOption.groupType === GroupType.Static || audienceOption.groupType === GroupType.Dynamic) &&
                <GroupAutoComplete
                    placeholder={t("Group.AudienceSelector.SelectGroupPlaceholder")}
                    selectedItems={selectedAudience?.autoComplete ? [selectedAudience.autoComplete] as AutoCompleteDataModel[] : []}
                    multiSelect={false}
                    onChange={(items: AutoCompleteDataModel[]) => {
                        if (items.length > 0) {
                            handleSelect({ id: items[0].key, name: items[0].label, groupType: GroupType.Static }, items[0]);
                        }
                        else {
                            handleSelect(undefined, undefined);
                        }
                    }}
                />
            }
            {(audienceOption.groupType === GroupType.UnitAllUsers || audienceOption.groupType === GroupType.UnitUsers) &&
                <UnitAutoCompleteHierarchical
                    label={t("Group.AudienceSelector.SelectUnitPlaceholder", { name: audienceOption.groupTypeName })}
                    selectedItems={selectedAudience?.autoComplete ? [selectedAudience.autoComplete] as UnitAutoCompleteHierarchicalModel[] : []}
                    multiSelect={false}
                    selectedMode="free"
                    unitTypeId={audienceOption.groupTypeId}
                    showDescendantOption={true}
                    onChange={(items: (AutoCompleteHierarchicalModel | UnitAutoCompleteHierarchicalModel)[]) => {
                        if (items.length > 0) {
                            const unit = items[0] as UnitAutoCompleteHierarchicalModel;
                            handleSelect(unit.includeDescendants ? unit.allUsersGroup : unit.usersGroup, items[0]);
                        }
                        else {
                            handleSelect(undefined, undefined);
                        }
                    }}
                    onError={e => MprcRequestSnackbarErrorReport(e)}
                />
            }
            {audienceOption.groupType === GroupType.OwnUser &&
                <UserAutoComplete
                    placeholder={t("Group.AudienceSelector.SelectUserPlaceholder")}
                    selectedItems={selectedAudience?.autoComplete ? [selectedAudience.autoComplete] as AutoCompleteDataModel[] : []}
                    multiSelect={false}
                    onChange={(items: AutoCompleteDataModel[]) => {
                        if (items.length > 0) {
                            handleSelect({ id: items[0].key, name: items[0].label, groupType: GroupType.OwnUser }, items[0]);
                        }
                        else {
                            handleSelect(undefined, undefined);
                        }
                    }}
                />
            }
            {audienceOption.groupType === GroupType.ChannelUsers &&
                <ChannelAutoComplete
                    placeholder={t("Group.AudienceSelector.SelectChannelPlaceholder")}
                    selectedItems={selectedAudience?.autoComplete ? [selectedAudience.autoComplete] as ChannelAutoCompleteDataModel[] : []}
                    multiSelect={false}
                    onChange={(items: ChannelAutoCompleteDataModel[]) => {
                        if (items.length > 0) {
                            handleSelect(items[0].usersGroup, items[0]);
                        }
                        else {
                            handleSelect(undefined, undefined);
                        }
                    }}
                />
            }
            {(audienceOption.groupType === GroupType.DirectSubordinates || audienceOption.groupType === GroupType.AllSubordinates) &&
                <ManagerAutoComplete
                    placeholder={t("Group.AudienceSelector.SelectManagerPlaceholder")}
                    selectedItems={selectedAudience?.autoComplete ? [selectedAudience.autoComplete] as AutoCompleteDataModel[] : []}
                    multiSelect={false}
                    onChange={(items: ManagerAutoCompleteDataModel[]) => {
                        if (items.length > 0) {
                            handleSelect(audienceOption.groupType === GroupType.DirectSubordinates ? items[0].directSubordinatesGroup : items[0].allSubordinatesGroup, items[0]);
                        }
                        else {
                            handleSelect(undefined, undefined);
                        }
                    }}
                />
            }
            {(audienceOption.groupType === GroupType.TopicRequiredUsers || audienceOption.groupType === GroupType.TopicOptionalUsers) &&
                <TopicAutoCompleteHierarchical
                    selectedMode="free"
                    placeholder={t("Group.AudienceSelector.SelectTopicPlaceholder")}
                    selectedItems={selectedAudience?.autoComplete ? [selectedAudience.autoComplete] as AutoCompleteHierarchicalModel[] : []}
                    multiSelect={false}
                    onChange={(items) => {
                        if (items.length > 0) {
                            var item = items[0] as TopicAutoCompleteHierarchicalModel;
                            handleSelect(audienceOption.groupType === GroupType.TopicOptionalUsers ? item.optionalGroup : item.requiredGroup, items[0]);
                        }
                        else {
                            handleSelect(undefined, undefined);
                        }
                    }}
                    onError={e => MprcRequestSnackbarErrorReport(e)}
                />
            }
            {audienceOption.groupType === GroupType.TrainingApprovedUsers &&
                <TrainingAutoComplete
                    placeholder={t("Group.AudienceSelector.SelectTrainingPlaceholder")}
                    selectedItems={selectedAudience?.autoComplete ? [selectedAudience.autoComplete] as AutoCompleteDataModel[] : []}
                    multiSelect={false}
                    onChange={(items: TrainingAutoCompleteDataModel[]) => {
                        if (items.length > 0) {
                            handleSelect(items[0].approvedUsersGroup, items[0]);
                        }
                        else {
                            handleSelect(undefined, undefined);
                        }
                    }}
                />
            }
        </>
    );
};
